<template>
  <div>
    <div class="relative">
      <AppLabel v-if="label" :for="id">{{ label }}</AppLabel>
      <div
        ref="comboboxRef"
        :class="[
          'min-h-[42px] bg-grey-10 dark:bg-dark-surface border border-0.5 rounded-md px-3 py-2 inline-flex w-full',
          error
            ? 'border-red-2 dark:border-dark-red'
            : 'border-grey-11 dark:border-dark-border',
          'focus-within:border-black dark:focus-within:border-dark-ink',
          disabled ? 'cursor-not-allowed bg-grey-9 dark:bg-dark-surface-2' : '',
        ]"
        role="combobox"
        :aria-expanded="dropdownIsOpen"
        :aria-owns="listboxId"
        aria-haspopup="listbox"
        :aria-controls="listboxId"
        :aria-activedescendant="
          dropdownIsOpen
            ? `${listboxId}-option-${focusedOptionIndex}`
            : undefined
        "
      >
        <input
          :id="id"
          ref="inputRef"
          :value="currentSearchTerm"
          :disabled="disabled"
          :placeholder="placeholder"
          :class="[
            'w-full bg-transparent focus:outline-hidden text-sm',
            disabled ? 'cursor-not-allowed' : '',
          ]"
          :aria-label="label || 'Address search'"
          :aria-invalid="!!error"
          :aria-describedby="
            error ? `${id}-error` : helpLabel ? `${id}-help` : undefined
          "
          role="searchbox"
          autocomplete="off"
          @input="onSearch"
          @keydown.esc.prevent="closeDropdown"
        />
        <div class="pointer-events-none flex items-center" aria-hidden="true">
          <mdicon
            v-if="error"
            name="alert-circle-outline"
            aria-label="Error"
            class="text-red-1 dark:text-dark-red pr-2"
          />
          <mdicon :name="chevronIcon" :size="20" aria-label="Expand" />
        </div>
      </div>

      <Teleport to="body">
        <ul
          v-if="dropdownIsOpen && hasSuggestions"
          :id="listboxId"
          ref="panelRef"
          :style="panelStyle"
          role="listbox"
          :aria-label="label || 'Address options'"
          class="z-50 bg-white dark:bg-dark-surface shadow-lg rounded-md text-base ring-1 ring-black/5 overflow-auto focus:outline-hidden sm:text-sm"
        >
          <li
            v-for="(suggestion, index) in suggestions"
            :id="`${listboxId}-option-${index}`"
            :key="index"
            role="option"
            :aria-selected="optionSelected(suggestion)"
            class="cursor-pointer text-sm select-none relative py-3 pl-3 pr-9 hover:bg-grey-9 dark:hover:bg-dark-surface-2 focus:outline-hidden focus:bg-grey-9 dark:focus:bg-dark-surface-2"
            :class="[
              optionSelected(suggestion)
                ? 'bg-grey-10 dark:bg-dark-surface-2'
                : '',
            ]"
            tabindex="0"
            @click="selectSuggestion(suggestion)"
            @keydown.enter="selectSuggestion(suggestion)"
            @keydown.space.prevent="selectSuggestion(suggestion)"
          >
            <AppHighlightedText
              :text="suggestion.text"
              :highlights="suggestion.highlight"
            />
            <span class="text-grey-11 dark:text-dark-ink-muted ml-1">{{
              suggestion.description
            }}</span>
          </li>
        </ul>
      </Teleport>
    </div>
    <AppErrorLabel
      v-if="error || addressError"
      :id="`${id}-error`"
      class="mt-1"
      >{{ error || addressError }}</AppErrorLabel
    >
    <AppHelpLabel v-if="helpLabel" :id="`${id}-help`" class="mt-1">{{
      helpLabel
    }}</AppHelpLabel>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useAddress, UNITED_KINGDOM } from '@/composables/useAddress'
import { useDropdownPosition } from '@/composables/useDropdownPosition'
import AppHighlightedText from '@/components/layout/text/AppHighlightedText.vue'

const ALLOWED_KEYS_FOR_STRING = [
  'address_line_1',
  'address_line_2',
  'address_line_3',
  'town',
  'county',
  'country',
  'postcode',
]

const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
  label: { type: String, default: '' },
  helpLabel: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  id: {
    type: String,
    default: () => `select-${Math.random().toString(36).substr(2, 9)}`,
  },
  placeholder: { type: String, default: 'Enter an address or postcode' },
  error: { type: String, default: '' },
  country: { type: Object, default: () => UNITED_KINGDOM },
})

const emit = defineEmits(['update:modelValue'])

const inputRef = ref(null)
const comboboxRef = ref(null)
const panelRef = ref(null)
const focusedOptionIndex = ref(-1)
const listboxId = computed(() => `${props.id}-listbox`)

const {
  suggestions,
  hasSuggestions,
  dropdownIsOpen,
  currentSearchTerm,
  error: addressError,
  fetchSuggestions,
  selectAddress,
  closeDropdown,
  setSearchTerm,
} = useAddress(() => props.country)

const { panelStyle } = useDropdownPosition({
  isOpen: dropdownIsOpen,
  triggerRef: comboboxRef,
})

const chevronIcon = computed(() => {
  return dropdownIsOpen.value ? 'chevron-up' : 'chevron-down'
})

const onSearch = (event) => {
  fetchSuggestions(event.target.value)
  focusedOptionIndex.value = -1
}

const selectSuggestion = async (suggestion) => {
  const address = await selectAddress(suggestion)
  emit('update:modelValue', address)
  focusedOptionIndex.value = -1
}

const optionSelected = (suggestion) => {
  return suggestion.address === currentSearchTerm.value
}

const handleClickOutside = (event) => {
  // The listbox is teleported to <body>, so a click inside it (scrollbar,
  // empty space) is not an outside click
  if (panelRef.value?.contains(event.target)) {
    return
  }
  closeDropdown()
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})

watch(
  () => props.modelValue,
  (newValue) => {
    if (!newValue.address_line_1) {
      setSearchTerm('')
      return
    }

    const searchTermValues = ALLOWED_KEYS_FOR_STRING.map(
      (key) => newValue[key]
    ).filter((value) => value)
    setSearchTerm(searchTermValues.join(', '))
  },
  { immediate: true, deep: true }
)
</script>
